import React, { useMemo } from "react";
import Image, { type StaticImageData } from "next/image";
import { Box, Flex, Text } from "@chakra-ui/react";

type TestimonialProps = {
  author?: string;
  job: string;
  company?: string;
  image?: StaticImageData | string;
  quote: string;
};

/**
 * Displays a quote with author details below. If no `author` name is provided,
 * just the `job` and `company` will be shown to provide a more anonymized quote.
 */
export function MdxTestimonial({ author, quote, job, company, image }: TestimonialProps) {
  const authorSection = useMemo(() => {
    if (author) {
      return (
        <Flex align="center" gap="smallest">
          {image && (
            <Box
              flex="none"
              boxSize="50px"
              display="block"
              position="relative"
              bg="new.blue.50"
              borderRadius="full"
              overflow="hidden"
            >
              <Image fill sizes="100%" src={image} alt={author} style={{ objectFit: "cover" }} />
            </Box>
          )}

          <Box>
            <Text textStyle="caption1" fontWeight="semibold" mb="tiniest">
              {author}
            </Text>
            <Text textStyle="caption1">
              {job}
              {company && <Text as="span"> at {company}</Text>}
            </Text>
          </Box>
        </Flex>
      );
    }

    return (
      <Text textStyle="caption1" fontWeight="semibold">
        {job}
        <Text as="span" fontWeight="normal">
          {company && ", " + company}
        </Text>
      </Text>
    );
  }, [author, job, company, image]);

  return (
    <Flex
      bg="new.blue.900"
      borderRadius="base"
      color="brand.text.dark"
      flexDir="column"
      gap="big"
      justifyContent="start"
      p={{ base: "big", md: "bigger" }}
    >
      <Text fontSize="brand.xl" lineHeight="1.4" fontWeight="medium" my={0}>
        “{quote}”
      </Text>

      {authorSection}
    </Flex>
  );
}
